import { Component, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MenuItem } from '@card-software/library';
import _ from 'lodash';
import { ConfirmationService, MessageService } from 'primeng/api';
import { environment } from '../environments/environment';
import {
  ClientPurchaseOrderRoles,
  DeliveryNoteRoles,
  ExternalRequisitionAdminGroup,
  ExternalRequisitionViewerGroup,
  IMEXAdminGroup,
  IMEXEditorGroup,
  IMEXViewerGroup,
  OrderReportingRoles,
  PurchaseOrderRoles,
  WorkOrderRoles,
} from './models/roles/roles-group';
import { UserAuthenticationService } from './services/user-authentication.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { MasterDataActionUtil } from './utilities/mater-data-action-util';
import { MasterDataService, RolesBootstrapService, Site, SiteBootstrapService, SiteService } from '@card-software/nexus-utilities';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  title = 'order-ui';
  isLoginPage = false;
  readonly environment = environment.environment;

  menuItems: MenuItem[] = [
    {
      displayName: 'Work Orders',
      routerLink: '/work-orders',
      redirect: false,
      children: [
        {
          displayName: 'Work Orders',
          routerLink: '/work-orders',
          redirect: true,
          roles: [WorkOrderRoles.WorkOrderViewer, WorkOrderRoles.WorkOrderEditor, WorkOrderRoles.WorkOrderAdmin],
        },
        {
          displayName: 'Client Purchase Orders',
          routerLink: '/client-purchase-orders',
          redirect: true,
          roles: [
            ClientPurchaseOrderRoles.ClientPurchaseOrderViewer,
            ClientPurchaseOrderRoles.ClientPurchaseOrderEditor,
            ClientPurchaseOrderRoles.ClientPurchaseOrderAdmin,
          ],
        },
      ],
    },
    {
      displayName: 'Purchase Orders',
      routerLink: '/purchase-orders',
      redirect: false,
      children: [
        {
          displayName: 'Purchase Orders',
          routerLink: '/purchase-orders',
          redirect: true,
          roles: [PurchaseOrderRoles.PurchaseOrderViewer, PurchaseOrderRoles.PurchaseOrderEditor, PurchaseOrderRoles.PurchaseOrderAdmin],
        },
      ],
    },

    {
      displayName: 'Invoices',
      routerLink: '/invoices',
      redirect: false,
      children: [
        {
          displayName: 'Invoices',
          routerLink: '/invoices',
          redirect: true,
          roles: [IMEXViewerGroup.InvoiceViewer, IMEXAdminGroup.InvoiceAdmin],
        },
        // Receipts (Receipt Pre-Invoice) is feature-flagged off in prod until sign-off.
        ...(environment.featureFlags.receipts
          ? [
              {
                displayName: 'Receipts',
                routerLink: '/receipts',
                redirect: true,
                roles: [IMEXViewerGroup.InvoiceViewer, IMEXAdminGroup.InvoiceAdmin],
              },
            ]
          : []),
      ],
    },
    {
      displayName: 'Delivery Notes',
      routerLink: '/delivery-notes',
      redirect: false,
      children: [
        {
          displayName: 'Delivery Notes',
          routerLink: '/delivery-notes',
          redirect: true,
          roles: [DeliveryNoteRoles.DeliveryNoteAdmin],
        },
      ],
    },
    {
      displayName: 'Requisitions',
      routerLink: '/requisitions',
      redirect: false,
      children: [
        {
          displayName: 'Requisitions',
          routerLink: '/requisitions',
          redirect: true,
          roles: [ExternalRequisitionAdminGroup.ExternalRequisitionAdmin, ExternalRequisitionViewerGroup.ExternalRequisitionViewer],
        },
      ],
    },
    {
      displayName: 'Shipments',
      routerLink: '/shipments',
      redirect: false,
      children: [
        {
          displayName: 'Shipments',
          routerLink: '/shipments',
          redirect: true,
          roles: [
            IMEXViewerGroup.ShipmentViewer,
            IMEXEditorGroup.ShipmentEditor,
            IMEXAdminGroup.ShipmentAdmin,
            IMEXAdminGroup.ShipmentSupervisor,
          ],
        },
      ],
    },
    {
      displayName: 'Reports',
      routerLink: '/order-reports',
      redirect: false,
      children: [
        {
          displayName: 'Customer Order Reports',
          routerLink: '/order-reports',
          redirect: true,
          roles: [OrderReportingRoles.OrderReportingViewer, OrderReportingRoles.OrderReportingAdmin],
        },
      ],
    },
  ];

  private router = inject(Router);
  private userAuthenticationService = inject(UserAuthenticationService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private siteBootstrapService = inject(SiteBootstrapService);
  private rolesBootstrapService = inject(RolesBootstrapService);

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isLoginPage = _.includes(event.url, '/login');
      }
    });
    GlobalMessageUtil.init(this.messageService);
    MasterDataActionUtil.init(this.confirmationService);

    const token = this.userAuthenticationService.getStoredJwtToken();
    if (token) {
      MasterDataService.initialize(environment.proxyUrls.itemApi, token);
      this.rolesBootstrapService.initializeRoles(token);
      this.siteBootstrapService.initializeSites(token);
    }
  }

  get isLoggedIn() {
    return this.userAuthenticationService.isUserAuthenticated;
  }

  get userLite() {
    return this.userAuthenticationService.userLite;
  }

  get userToken() {
    return this.userAuthenticationService.getStoredJwtToken() || '';
  }

  get showSidebar(): boolean {
    const isLoginPage = this.router.url.includes('/login');
    return this.userAuthenticationService.isUserAuthenticated && !isLoginPage;
  }

  get sites(): Site[] {
    return SiteService.getSites();
  }

  get selectedSiteId(): string {
    return SiteService.getSelectedSiteId() || '';
  }

  onSiteChanged(siteId: string) {
    SiteService.setSelectedSite(siteId);
  }

  onLogout() {
    this.userAuthenticationService.logout();
  }
}
